import * as THREE from 'three';
import { Hologram } from './Hologram';
import { FloatingData } from './FloatingData';

// Re-using ScreenManager implementation from main.ts, so no import needed here.
// SpaceshipRoom is just the visual container.

export class SpaceshipRoom {
    public group: THREE.Group;
    private particles!: THREE.Points;

    // Interactive Elements
    private hologram!: Hologram;
    private floatingData!: FloatingData;

    private readonly roomRadius = 10;
    private readonly roomHeight = 5;
    private readonly numSides = 8;

    constructor() {
        this.group = new THREE.Group();
        this.group.name = 'spaceship_room';

        this.createEnvironmentMap();

        // Add "Tech" particles (Data cloud)
        this.createParticles();

        // Add Interactive Elements
        this.hologram = new Hologram();
        this.hologram.mesh.position.set(0, -0.5, 0); // Center room
        this.group.add(this.hologram.mesh);

        this.floatingData = new FloatingData(300);
        this.group.add(this.floatingData.mesh);

        this.createFloor();
        this.createWalls();
        this.createCeiling();
        this.createTechDetails();
        this.createAccentLights();
        // This method creates the "ambient_particles" (background dust)
        this.createAmbientDetails();
    }

    private createParticles(): void {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        for (let i = 0; i < 1000; i++) {
            const x = (Math.random() - 0.5) * 20;
            const y = (Math.random() - 0.5) * 10;
            const z = (Math.random() - 0.5) * 20;
            vertices.push(x, y, z);
        }
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

        const material = new THREE.PointsMaterial({
            color: 0x00d4ff,
            size: 0.05,
            transparent: true,
            opacity: 0.3,
            blending: THREE.AdditiveBlending
        });

        this.particles = new THREE.Points(geometry, material);
        this.particles.name = 'data_cloud';
        this.group.add(this.particles);
    }

    public update(deltaTime: number): void {
        const time = Date.now() * 0.001;

        // Animate ambient particles (background dust)
        const ambientParticles = this.group.getObjectByName('ambient_particles') as THREE.Points;
        if (ambientParticles) {
            const positions = ambientParticles.geometry.attributes.position.array as Float32Array;
            for (let i = 0; i < positions.length; i += 3) {
                positions[i + 1] += Math.sin(time * 0.3 + i * 0.1) * 0.002;
                if (positions[i + 1] > this.roomHeight) positions[i + 1] = 0;
                else if (positions[i + 1] < 0) positions[i + 1] = this.roomHeight;
            }
            ambientParticles.geometry.attributes.position.needsUpdate = true;
        }

        // Animate data cloud particles
        if (this.particles) {
            this.particles.rotation.y = time * 0.05;
        }

        // Animate holographic elements (tech details around room)
        const techDetails = this.group.getObjectByName('tech_details');
        if (techDetails) {
            techDetails.traverse((child) => {
                if (child.name.startsWith('holo_')) {
                    child.rotation.z += 0.01;
                    child.rotation.x += 0.005;
                    ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity =
                        0.3 + Math.sin(time * 2 + parseFloat(child.name.split('_')[1])) * 0.2;
                }
            });
        }

        // Update interactive elements
        if (this.hologram) this.hologram.update(deltaTime);
        if (this.floatingData) this.floatingData.update(deltaTime);
    }

    private createEnvironmentMap(): void {
        // Create a simple gradient environment for reflections
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d')!;

        // Create gradient
        const gradient = ctx.createLinearGradient(0, 0, 0, 256);
        gradient.addColorStop(0, '#1a1a2e');
        gradient.addColorStop(0.5, '#0d0d1a');
        gradient.addColorStop(1, '#050510');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 256, 256);

        // Add some subtle noise/stars
        ctx.fillStyle = 'rgba(0, 212, 255, 0.1)';
        for (let i = 0; i < 50; i++) {
            const x = Math.random() * 256;
            const y = Math.random() * 256;
            ctx.beginPath();
            ctx.arc(x, y, 1, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    private createFloor(): void {
        // Create hexagonal floor panels for tech look
        const floorGroup = new THREE.Group();
        floorGroup.name = 'floor_group';

        // Main floor base
        const floorGeometry = new THREE.CircleGeometry(this.roomRadius + 1, 64);
        const floorMaterial = new THREE.MeshStandardMaterial({
            color: 0x0a0a12,
            metalness: 0.9,
            roughness: 0.1,
        });
        const floor = new THREE.Mesh(floorGeometry, floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = -0.01;
        floor.receiveShadow = true;
        floorGroup.add(floor);

        // Create floor panels in a grid pattern
        const panelSize = 1.5;
        const panelGap = 0.03;

        for (let x = -this.roomRadius; x <= this.roomRadius; x += panelSize + panelGap) {
            for (let z = -this.roomRadius; z <= this.roomRadius; z += panelSize + panelGap) {
                const dist = Math.sqrt(x * x + z * z);
                if (dist < this.roomRadius - 0.5) {
                    this.createFloorPanel(floorGroup, x, z, panelSize, dist);
                }
            }
        }

        // Center platform with glow
        const platformRadius = 2.5;
        const platformGeometry = new THREE.CylinderGeometry(platformRadius, platformRadius + 0.1, 0.15, 32);
        const platformMaterial = new THREE.MeshStandardMaterial({
            color: 0x15151f,
            metalness: 0.95,
            roughness: 0.05,
        });
        const platform = new THREE.Mesh(platformGeometry, platformMaterial);
        platform.position.y = 0.075;
        platform.receiveShadow = true;
        floorGroup.add(platform);

        // Platform edge glow
        const glowRingGeometry = new THREE.TorusGeometry(platformRadius + 0.05, 0.02, 16, 64);
        const glowRingMaterial = new THREE.MeshBasicMaterial({
            color: 0x00d4ff,
            transparent: true,
            opacity: 0.8,
        });
        const glowRing = new THREE.Mesh(glowRingGeometry, glowRingMaterial);
        glowRing.rotation.x = Math.PI / 2;
        glowRing.position.y = 0.16;
        floorGroup.add(glowRing);

        // Inner platform rings
        for (let i = 1; i <= 3; i++) {
            const ringGeometry = new THREE.RingGeometry(platformRadius * 0.2 * i, platformRadius * 0.2 * i + 0.02, 64);
            const ringMaterial = new THREE.MeshBasicMaterial({
                color: 0x00d4ff,
                transparent: true,
                opacity: 0.3 - i * 0.08,
                side: THREE.DoubleSide,
            });
            const ring = new THREE.Mesh(ringGeometry, ringMaterial);
            ring.rotation.x = -Math.PI / 2;
            ring.position.y = 0.16;
            floorGroup.add(ring);
        }

        this.group.add(floorGroup);
    }

    private createFloorPanel(parent: THREE.Group, x: number, z: number, size: number, dist: number): void {
        const panelGeometry = new THREE.BoxGeometry(size - 0.02, 0.05, size - 0.02);

        // Vary panel color based on distance from center
        const brightness = 0.08 + (1 - dist / this.roomRadius) * 0.04;
        const panelMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color(brightness, brightness, brightness + 0.02),
            metalness: 0.8,
            roughness: 0.3,
        });

        const panel = new THREE.Mesh(panelGeometry, panelMaterial);
        panel.position.set(x, 0.025, z);
        panel.receiveShadow = true;
        parent.add(panel);

        // Add subtle edge glow to some panels
        if (Math.random() > 0.85) {
            const edgeGeometry = new THREE.BoxGeometry(size - 0.01, 0.01, size - 0.01);
            const edgeMaterial = new THREE.MeshBasicMaterial({
                color: 0x00d4ff,
                transparent: true,
                opacity: 0.15,
            });
            const edge = new THREE.Mesh(edgeGeometry, edgeMaterial);
            edge.position.set(x, 0.055, z);
            parent.add(edge);
        }
    }

    private createWalls(): void {
        const angleStep = (Math.PI * 2) / this.numSides;

        for (let i = 0; i < this.numSides; i++) {
            const midAngle = i * angleStep + angleStep / 2;
            const wallWidth = 2 * this.roomRadius * Math.sin(angleStep / 2);

            // Main wall panel
            const wallGroup = new THREE.Group();
            wallGroup.name = `wall_${i}`;

            // Back wall layer (dark)
            const backWallGeo = new THREE.BoxGeometry(wallWidth, this.roomHeight, 0.1);
            const backWallMat = new THREE.MeshStandardMaterial({
                color: 0x080810,
                metalness: 0.7,
                roughness: 0.5,
            });
            const backWall = new THREE.Mesh(backWallGeo, backWallMat);
            backWall.position.z = -0.05;
            wallGroup.add(backWall);

            // Wall panels (layered look)
            const numPanels = 3;
            const panelHeight = (this.roomHeight - 0.4) / numPanels;

            for (let p = 0; p < numPanels; p++) {
                const panelWidth = wallWidth - 0.6;
                const panelGeo = new THREE.BoxGeometry(panelWidth, panelHeight - 0.1, 0.08);
                const panelMat = new THREE.MeshStandardMaterial({
                    color: 0x12121a + p * 0x020202,
                    metalness: 0.85,
                    roughness: 0.15,
                });
                const panel = new THREE.Mesh(panelGeo, panelMat);
                panel.position.y = -this.roomHeight / 2 + 0.2 + panelHeight / 2 + p * panelHeight;
                panel.position.z = 0.04;
                panel.castShadow = true;
                panel.receiveShadow = true;
                wallGroup.add(panel);
            }

            // Vertical accent strips
            const stripPositions = [-wallWidth / 2 + 0.15, wallWidth / 2 - 0.15];
            stripPositions.forEach(xPos => {
                const stripGeo = new THREE.BoxGeometry(0.02, this.roomHeight - 0.4, 0.1);
                const stripMat = new THREE.MeshBasicMaterial({
                    color: 0x00d4ff,
                    transparent: true,
                    opacity: 0.6,
                });
                const strip = new THREE.Mesh(stripGeo, stripMat);
                strip.position.set(xPos, 0, 0.05);
                wallGroup.add(strip);
            });

            // Horizontal accent at top
            const topStripGeo = new THREE.BoxGeometry(wallWidth - 0.2, 0.02, 0.1);
            const topStripMat = new THREE.MeshBasicMaterial({
                color: 0x00d4ff,
                transparent: true,
                opacity: 0.4,
            });
            const topStrip = new THREE.Mesh(topStripGeo, topStripMat);
            topStrip.position.y = this.roomHeight / 2 - 0.15;
            topStrip.position.z = 0.05;
            wallGroup.add(topStrip);

            // Position wall group
            wallGroup.position.x = Math.sin(midAngle) * this.roomRadius;
            wallGroup.position.z = Math.cos(midAngle) * this.roomRadius;
            wallGroup.position.y = this.roomHeight / 2;
            wallGroup.rotation.y = -midAngle;

            this.group.add(wallGroup);
        }
    }

    private createCeiling(): void {
        const ceilingGroup = new THREE.Group();
        ceilingGroup.name = 'ceiling_group';

        // Main ceiling
        const ceilingGeometry = new THREE.CircleGeometry(this.roomRadius + 0.5, 64);
        const ceilingMaterial = new THREE.MeshStandardMaterial({
            color: 0x0a0a12,
            metalness: 0.7,
            roughness: 0.3,
            side: THREE.DoubleSide,
        });
        const ceiling = new THREE.Mesh(ceilingGeometry, ceilingMaterial);
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.y = this.roomHeight;
        ceilingGroup.add(ceiling);

        // Concentric ceiling rings
        const ringRadii = [8, 5.5, 3];

        ringRadii.forEach((radius, idx) => {
            const ringGeo = new THREE.TorusGeometry(radius, 0.08, 8, 64);
            const ringMat = new THREE.MeshStandardMaterial({
                color: 0x1a1a25,
                metalness: 0.9,
                roughness: 0.1,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.rotation.x = Math.PI / 2;
            ring.position.y = this.roomHeight - 0.1 - idx * 0.05;
            ceilingGroup.add(ring);

            // Glow under ring
            const glowGeo = new THREE.TorusGeometry(radius, 0.03, 8, 64);
            const glowMat = new THREE.MeshBasicMaterial({
                color: 0x00d4ff,
                transparent: true,
                opacity: 0.3 - idx * 0.08,
            });
            const glow = new THREE.Mesh(glowGeo, glowMat);
            glow.rotation.x = Math.PI / 2;
            glow.position.y = this.roomHeight - 0.15 - idx * 0.05;
            ceilingGroup.add(glow);
        });

        // Central light fixture
        const fixtureGroup = new THREE.Group();

        // Dome
        const domeGeo = new THREE.SphereGeometry(1.2, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2);
        const domeMat = new THREE.MeshStandardMaterial({
            color: 0x15151f,
            metalness: 0.95,
            roughness: 0.05,
            side: THREE.DoubleSide,
        });
        const dome = new THREE.Mesh(domeGeo, domeMat);
        dome.rotation.x = Math.PI;
        fixtureGroup.add(dome);

        // Light ring around dome
        const lightRingGeo = new THREE.TorusGeometry(1.3, 0.05, 16, 64);
        const lightRingMat = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.9,
        });
        const lightRing = new THREE.Mesh(lightRingGeo, lightRingMat);
        lightRing.rotation.x = Math.PI / 2;
        lightRing.position.y = -0.1;
        fixtureGroup.add(lightRing);

        fixtureGroup.position.y = this.roomHeight;
        ceilingGroup.add(fixtureGroup);

        this.group.add(ceilingGroup);
    }

    private createTechDetails(): void {
        // Add tech panels and details around the room
        const detailGroup = new THREE.Group();
        detailGroup.name = 'tech_details';

        // Corner pillars
        const angleStep = (Math.PI * 2) / this.numSides;
        for (let i = 0; i < this.numSides; i++) {
            const angle = i * angleStep;
            const pillarRadius = this.roomRadius - 0.3;

            const pillarGeo = new THREE.BoxGeometry(0.15, this.roomHeight - 0.5, 0.15);
            const pillarMat = new THREE.MeshStandardMaterial({
                color: 0x1a1a25,
                metalness: 0.9,
                roughness: 0.1,
            });
            const pillar = new THREE.Mesh(pillarGeo, pillarMat);
            pillar.position.set(
                Math.sin(angle) * pillarRadius,
                this.roomHeight / 2,
                Math.cos(angle) * pillarRadius
            );
            pillar.rotation.y = -angle;
            detailGroup.add(pillar);

            // Pillar light strip
            const stripGeo = new THREE.BoxGeometry(0.02, this.roomHeight - 0.8, 0.16);
            const stripMat = new THREE.MeshBasicMaterial({
                color: 0x7b2fff,
                transparent: true,
                opacity: 0.5,
            });
            const strip = new THREE.Mesh(stripGeo, stripMat);
            strip.position.copy(pillar.position);
            strip.rotation.y = -angle;
            detailGroup.add(strip);
        }

        // Add some floating holographic elements
        for (let i = 0; i < 5; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 3 + Math.random() * 4;
            const height = 2 + Math.random() * 2;

            const holoGeo = new THREE.RingGeometry(0.1, 0.12, 32);
            const holoMat = new THREE.MeshBasicMaterial({
                color: 0x00d4ff,
                transparent: true,
                opacity: 0.4,
                side: THREE.DoubleSide,
            });
            const holo = new THREE.Mesh(holoGeo, holoMat);
            holo.position.set(
                Math.sin(angle) * radius,
                height,
                Math.cos(angle) * radius
            );
            holo.rotation.x = Math.random() * Math.PI;
            holo.rotation.y = Math.random() * Math.PI;
            holo.name = `holo_${i}`;
            detailGroup.add(holo);
        }

        this.group.add(detailGroup);
    }

    private createAccentLights(): void {
        // Ambient light
        const ambientLight = new THREE.AmbientLight(0x4060a0, 0.6);
        this.group.add(ambientLight);

        // Hemisphere light
        const hemiLight = new THREE.HemisphereLight(0x8090b0, 0x202040, 0.8);
        hemiLight.position.y = this.roomHeight;
        this.group.add(hemiLight);

        // Central spotlight from ceiling
        const spotlight = new THREE.SpotLight(0xffffff, 2.5, 15, Math.PI / 5, 0.3, 0.5);
        spotlight.position.set(0, this.roomHeight - 0.5, 0);
        spotlight.target.position.set(0, 0, 0);
        spotlight.castShadow = true;
        spotlight.shadow.mapSize.width = 2048;
        spotlight.shadow.mapSize.height = 2048;
        spotlight.shadow.camera.near = 0.5;
        spotlight.shadow.camera.far = 20;
        this.group.add(spotlight);
        this.group.add(spotlight.target);

        // Colored accent lights around the room
        const accentColors = [
            { color: 0x00d4ff, pos: [6, 2.5, 0] },
            { color: 0x7b2fff, pos: [-6, 2.5, 0] },
            { color: 0xff2d7a, pos: [0, 2.5, 6] },
            { color: 0x00ff88, pos: [0, 2.5, -6] },
        ];

        accentColors.forEach(({ color, pos }) => {
            const light = new THREE.PointLight(color, 0.5, 12);
            light.position.set(pos[0], pos[1], pos[2]);
            this.group.add(light);
        });

        // Rim lights for dramatic effect
        const rimLightPositions = [
            [8, 4, 0], [-8, 4, 0], [0, 4, 8], [0, 4, -8]
        ];
        rimLightPositions.forEach(pos => {
            const rimLight = new THREE.PointLight(0x4060a0, 0.3, 8);
            rimLight.position.set(pos[0], pos[1], pos[2]);
            this.group.add(rimLight);
        });
    }

    private createAmbientDetails(): void {
        // Floating particles
        const particleCount = 150;
        const positions = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);

        for (let i = 0; i < particleCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * this.roomRadius * 0.8;

            positions[i * 3] = Math.sin(angle) * radius;
            positions[i * 3 + 1] = Math.random() * this.roomHeight;
            positions[i * 3 + 2] = Math.cos(angle) * radius;

            // Random cyan or purple
            if (Math.random() > 0.5) {
                colors[i * 3] = 0;
                colors[i * 3 + 1] = 0.83;
                colors[i * 3 + 2] = 1;
            } else {
                colors[i * 3] = 0.48;
                colors[i * 3 + 1] = 0.18;
                colors[i * 3 + 2] = 1;
            }
        }

        const particleGeometry = new THREE.BufferGeometry();
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const particleMaterial = new THREE.PointsMaterial({
            size: 0.03,
            transparent: true,
            opacity: 0.6,
            vertexColors: true,
            blending: THREE.AdditiveBlending,
        });

        const particles = new THREE.Points(particleGeometry, particleMaterial);
        particles.name = 'ambient_particles';
        this.group.add(particles);
    }

    public dispose(): void {
        this.group.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
                obj.geometry.dispose();
                if (Array.isArray(obj.material)) {
                    obj.material.forEach(m => m.dispose());
                } else {
                    obj.material.dispose();
                }
            }
        });
    }
}
